import { useEffect, useRef } from 'react';

import { usePublishedDiscourseState, usePublishedDiscourseActions } from './PublishedDiscourseContext';

import { getPublishedDocuments } from './restApi';

const PublishedDiscourseLoader = () => {
  const state = usePublishedDiscourseState();
  const { initializeState, onResponse, onError } = usePublishedDiscourseActions();
  const initializedRef = useRef(false);
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      initializeState();
    }
  }, []);

  useEffect(() => {
    if (fetchingRef.current) return;

    const documents = state?.documents || [];
    if (documents.length === 0 && (state?.moreDocumentsAvailable ?? true)) {
      fetchingRef.current = true;
      getPublishedDocuments({
        pageNumber: state?.pageNumber || 1,
        onResponse: (data) => {
          fetchingRef.current = false;
          onResponse(data);
        },
        onError: (feedback) => {
          fetchingRef.current = false;
          onError(feedback);
        },
      });
    }
  // state?.documents is included as a dependency for the initial load
  // sequence where documents data goes from empty to populated:
  //
  // 1. Component mounts, state is {} (empty object from AppContext)
  // 2. First effect runs initializeState(), which initializes documents to []
  // 3. documents goes from undefined → [], so the second effect fires
  // 4. documents is empty, so it calls getPublishedDocuments
  // 5. The fetch completes, onResponse appends documents to the documents array
  // 6. documents array is now populated
  //
  // At step 6, without documents in the dependency array, the effect
  // wouldn't re-run. That's actually fine in the happy path — we don't want it
  // to re-run since documents already loaded. But the effect's closure from step 3
  // still has the old empty documents. If anything else causes a re-render and
  // React decides to re-run the effect (e.g., strict mode), it would see stale
  // data and fetch again.
  //
  // So honestly — it's mostly defensive. The fetchingRef is doing the heavy
  // lifting to prevent duplicate fetches. This dependency just ensures the
  // effect's closure has fresh data if it does re-run, making the effect more
  // correct in terms of React's mental model — your dependencies should reflect
  // what the effect actually reads.
  }, [state?.documents]);

  return null;
};

export default PublishedDiscourseLoader;
